import { useIntervalEffect } from "@react-hookz/web";
import { useGetVehicleDocumentQuery } from "@/redux/apis/vehicle/vehicleApi";
import { getErrorMessage, getIsManualExpiryDate } from "../EditVehicle/helpers";

interface UseVehicleDocumentsParams {
  vehicleId: string;
  insuranceId?: string;
  accreditationId?: string;
  documentRetryCount: { insurance: number; accreditation: number };
  setDocumentRetryCount: React.Dispatch<React.SetStateAction<{ insurance: number; accreditation: number }>>;
}

// Matches app-driver's behaviour: after this many polls while still PROCESSING,
// fall back to manual expiry-date entry so the user isn't hard-blocked when the
// document-review pipeline stalls.
const MAX_PROCESSING_RETRIES = 5;

const PROCESSING_FALLBACK_MESSAGE =
  "File couldn't be verified automatically. Please manually enter the date and confirm that you are responsible for the entered date.";

export const useVehicleDocuments = ({
  vehicleId,
  insuranceId,
  accreditationId,
  documentRetryCount,
  setDocumentRetryCount,
}: UseVehicleDocumentsParams) => {
  const {
    data: insuranceDoc,
    isFetching: isLoadingInsurance,
    refetch: refetchInsurance,
  } = useGetVehicleDocumentQuery(
    { vehicleId, vehicleDocumentId: insuranceId || "" },
    { skip: !insuranceId }
  );

  const {
    data: accreditationDoc,
    isFetching: isLoadingAccreditation,
    refetch: refetchAccreditation,
  } = useGetVehicleDocumentQuery(
    { vehicleId, vehicleDocumentId: accreditationId || "" },
    { skip: !accreditationId }
  );

  useIntervalEffect(() => {
    if (
      insuranceId &&
      insuranceDoc?.approvalStatus === "PROCESSING" &&
      documentRetryCount.insurance < MAX_PROCESSING_RETRIES
    ) {
      refetchInsurance();
      setDocumentRetryCount((prev) => ({ ...prev, insurance: prev.insurance + 1 }));
    }

    if (
      accreditationId &&
      accreditationDoc?.approvalStatus === "PROCESSING" &&
      documentRetryCount.accreditation < MAX_PROCESSING_RETRIES
    ) {
      refetchAccreditation();
      setDocumentRetryCount((prev) => ({ ...prev, accreditation: prev.accreditation + 1 }));
    }
  }, 5000);

  const insuranceProcessingExhausted =
    !!insuranceId && insuranceDoc?.approvalStatus === "PROCESSING" && documentRetryCount.insurance >= MAX_PROCESSING_RETRIES;

  const accreditationProcessingExhausted =
    !!accreditationId && accreditationDoc?.approvalStatus === "PROCESSING" && documentRetryCount.accreditation >= MAX_PROCESSING_RETRIES;

  const insuranceManualExpiryDate =
    insuranceProcessingExhausted || (!!insuranceId && !!insuranceDoc && getIsManualExpiryDate(insuranceDoc));

  const accreditationManualExpiryDate =
    accreditationProcessingExhausted || (!!accreditationId && !!accreditationDoc && getIsManualExpiryDate(accreditationDoc));

  const insuranceErrorMessage = insuranceProcessingExhausted
    ? PROCESSING_FALLBACK_MESSAGE
    : insuranceId && insuranceDoc
      ? getErrorMessage(insuranceDoc)
      : "";

  const accreditationErrorMessage = accreditationProcessingExhausted
    ? PROCESSING_FALLBACK_MESSAGE
    : accreditationId && accreditationDoc
      ? getErrorMessage(accreditationDoc)
      : "";

  const isProcessing =
    (insuranceDoc?.approvalStatus === "PROCESSING" && !insuranceProcessingExhausted) ||
    (accreditationDoc?.approvalStatus === "PROCESSING" && !accreditationProcessingExhausted);

  return {
    insurance: {
      document: insuranceId ? insuranceDoc : undefined,
      errorMessage: insuranceErrorMessage,
      manualExpiryDate: insuranceManualExpiryDate,
      isLoading: isLoadingInsurance,
    },
    accreditation: {
      document: accreditationId ? accreditationDoc : undefined,
      errorMessage: accreditationErrorMessage,
      manualExpiryDate: accreditationManualExpiryDate,
      isLoading: isLoadingAccreditation,
    },
    isLoading: isLoadingInsurance || isLoadingAccreditation,
    isProcessing,
  };
};
